import { create } from 'zustand';

interface Appointment {
    id: string;
    po_number: string;
    carrier_name: string;
    status: string;
    scheduled_time: string;
    dock_id?: string | null;
    type?: string;
    cargo_type?: string; // e.g. 'Palletized'
    sku_count?: number;
    estimated_arrival?: string;
    transit_impact?: string; // 'NONE' | 'MODERATE' | 'SEVERE'
}

interface UIState {
    // PO Wizard
    isPOWizardOpen: boolean;
    openPOWizard: () => void;
    closePOWizard: () => void;
    togglePOWizard: (isOpen: boolean) => void;
    setPOWizardOpen: (isOpen: boolean) => void;

    // Appointment Detail Modal
    selectedAppointment: Appointment | null;
    selectedDockId: string | null;
    isAppointmentDetailOpen: boolean;
    appointmentDetailScrollToRisk: boolean;
    openAppointmentDetail: (appointment: Appointment, options?: { scrollToRisk?: boolean; initialTab?: 'overview' | 'documents' | 'messages' | 'notes' }) => void;
    openDockDetail: (dockId: string) => void;
    closeAppointmentDetail: () => void;
    setAppointmentDetailScrollToRisk: (scrollToRisk: boolean) => void;
    appointmentDetailInitialTab: 'overview' | 'documents' | 'messages' | 'notes';

    // Check-In Modal
    isCheckInModalOpen: boolean;
    toggleCheckInModal: (isOpen: boolean) => void;

    // Multi-subscriber action success bus
    actionSuccessListeners: Set<() => void>;
    registerActionSuccessListener: (fn: () => void) => void;
    unregisterActionSuccessListener: (fn: () => void) => void;
    notifyActionSuccess: () => void;

    // Approval-without-dock handoff (AppointmentDetailDialog → Dashboard dock modal)
    pendingApprovalAppointment: Appointment | null;
    setPendingApprovalAppointment: (appt: Appointment) => void;
    clearPendingApprovalAppointment: () => void;
}

export const useUIStore = create<UIState>((set) => ({
    // PO Wizard
    isPOWizardOpen: false,
    openPOWizard: () => set({ isPOWizardOpen: true }),
    closePOWizard: () => set({ isPOWizardOpen: false }),
    togglePOWizard: (isOpen: boolean) => set({ isPOWizardOpen: isOpen }),
    setPOWizardOpen: (isOpen: boolean) => set({ isPOWizardOpen: isOpen }),

    // Appointment Detail
    selectedAppointment: null,
    selectedDockId: null,
    isAppointmentDetailOpen: false,
    appointmentDetailScrollToRisk: false,
    appointmentDetailInitialTab: 'overview',
    openAppointmentDetail: (appointment, options) => set({
        selectedAppointment: appointment,
        selectedDockId: appointment.dock_id || null,
        isAppointmentDetailOpen: true,
        appointmentDetailScrollToRisk: !!options?.scrollToRisk,
        appointmentDetailInitialTab: options?.initialTab ?? 'overview',
    }),
    openDockDetail: (dockId) => set({
        selectedDockId: dockId,
        selectedAppointment: null, // Clear prev appointment to force loading state if component handles it
        isAppointmentDetailOpen: true,
        appointmentDetailScrollToRisk: false
    }),
    closeAppointmentDetail: () => set({
        selectedAppointment: null,
        selectedDockId: null,
        isAppointmentDetailOpen: false,
        appointmentDetailScrollToRisk: false,
        appointmentDetailInitialTab: 'overview',
    }),
    setAppointmentDetailScrollToRisk: (scrollToRisk) => set({ appointmentDetailScrollToRisk: scrollToRisk }),

    // Check-In Modal
    isCheckInModalOpen: false,
    toggleCheckInModal: (isOpen: boolean) => set({ isCheckInModalOpen: isOpen }),

    // Multi-subscriber action success bus
    actionSuccessListeners: new Set<() => void>(),
    registerActionSuccessListener: (fn) => {
        useUIStore.getState().actionSuccessListeners.add(fn);
    },
    unregisterActionSuccessListener: (fn) => {
        useUIStore.getState().actionSuccessListeners.delete(fn);
    },
    notifyActionSuccess: () => {
        useUIStore.getState().actionSuccessListeners.forEach(fn => fn());
    },

    // Approval-without-dock handoff
    pendingApprovalAppointment: null,
    setPendingApprovalAppointment: (appt) => set({ pendingApprovalAppointment: appt }),
    clearPendingApprovalAppointment: () => set({ pendingApprovalAppointment: null }),
}));
